<template>
  <b-container fluid class="grm-body ansi-dataset-panel">
    <b-row v-if="releases.length === 0" class="justify-content-center">
      <b-col cols="12" class="text-center grm-empty">
        <div class="grm-empty-icon">◎</div>
        <p>No releases available</p>
      </b-col>
    </b-row>

    <b-row class="grm-grid">
      <b-col
        v-for="(rel, index) in releases"
        :key="index"
        class="grm-card-col mb-3"
      >
        <div
          :class="['grm-card', { 'grm-card--expanded': isExpanded(index) }]"
          @click="updateANSIDisplayDataset(rel, `ansi_dataset_column_${index}`, rel.extent_description)"
        >
          <!-- Main card row -->
          <div class="grm-card-row d-flex align-items-center w-100">
            <div class="grm-map-preview">
              <div class="grm-map-glow"></div>
              <img
                v-if="getRegionImage(rel.extent_description)"
                :src="getRegionImage(rel.extent_description)"
                :alt="rel.extent_description"
                class="grm-map-img"
              />
              <div v-else class="grm-map-placeholder">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="grm-globe-icon">
                  <circle cx="12" cy="12" r="10" stroke="#4db8ff" stroke-width="1.5"/>
                  <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" stroke="#4db8ff" stroke-width="1.5"/>
                </svg>
              </div>
            </div>

            <div class="grm-card-info">
              <div class="grm-region-name">{{ rel.extent_description }}</div>
              <div class="grm-release-label" v-if="rel.release_label">{{ rel.release_label }}</div>
              <div class="grm-release-date">
                <span class="grm-date-dot"></span>
                {{ formatDate(rel.release_date) }}
              </div>
            </div>

            <div class="grm-card-action">
              <span class="grm-action-label">{{ isExpanded(index) ? 'Pick' : 'Select' }}</span>
              <svg class="grm-action-arrow" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Selset expansion -->
          <div
            v-if="isExpanded(index) && selsets.length > 0"
            class="grm-selsets"
            @click.stop
          >
            <div class="grm-selsets-label">Choose a selection set</div>
            <div class="grm-selsets-row">
              <button
                v-for="(selset, si) in selsets"
                :key="si"
                class="grm-selset-btn"
                @click="updateAnsiDisplaySelset(selset)"
              >
                {{ selset.selset }}
              </button>
            </div>
          </div>

          <div class="grm-card-shine"></div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<style>
/* Stack the col-6 cards vertically, one row after another */
.ansi-dataset-panel .grm-grid {
  flex-direction: column;
}

.grm-card--expanded {
  flex-direction: column;
  align-items: stretch;
}

.grm-card-row {
  flex-shrink: 0;
}

.grm-selsets {
  border-top: 1px solid rgba(77, 184, 255, 0.15);
  padding: 10px 14px 12px;
  width: 100%;
}

.grm-selsets-label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  /* color: rgba(77, 184, 255, 0.7); */
  color: #063c6d;
  margin-bottom: 8px;
}

.grm-selsets-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.grm-selset-btn {
  background: rgba(77, 184, 255, 0.1);
  border: 1px solid rgba(77, 184, 255, 0.3);
  color: #063c6d;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.grm-selset-btn:hover {
  background: rgba(77, 184, 255, 0.22);
  border-color: rgba(77, 184, 255, 0.6);
}
</style>

<script>
import axios from 'axios'
import {get as projGet, transform, transformExtent} from 'ol/proj'
import View from 'ol/View'
import australiaImg from '../../../assets/extents/Australia.svg'
import europeImg from '../../../assets/extents/Europe.svg'
import japanImg from '../../../assets/extents/Japan.svg'
import middleEastImg from '../../../assets/extents/MiddleEast.svg'
import newZealandImg from '../../../assets/extents/NewZealand.svg'
import northAmericaImg from '../../../assets/extents/NorthAmerica.svg'
import southKoreaImg from '../../../assets/extents/SouthKorea.svg'

const REGION_MAP = [
  { keys: ['australia'],                                                img: australiaImg },
  { keys: ['europe'],                                                   img: europeImg },
  { keys: ['japan'],                                                    img: japanImg },
  { keys: ['middle east', 'middleeast'],                               img: middleEastImg },
  { keys: ['new zealand', 'newzealand'],                               img: newZealandImg },
  { keys: ['north america', 'northamerica', 'usa', 'united states'],  img: northAmericaImg },
  { keys: ['south korea', 'southkorea', 'korea'],                     img: southKoreaImg },
]

export default {
  name: 'MMP_ANSI_DatasetPanel',
  props: [
    'apiUrl',
    'customerName',
    'currentExtent',
    'releases',
    'selectedDataset',
    'user',
    'ansiLayersButtonCtrl'
  ],
  data() {
    return {
      extents: [],
      selsets: [],
      selsetsLoaded: {
        id: '',
        loaded: false
      }
    }
  },
  methods: {
    isExpanded(index) {
      return (
        this.selsetsLoaded.id === `ansi_dataset_column_${index}` &&
        this.selsetsLoaded.loaded
      )
    },
    getRegionImage(extentDescription) {
      if (!extentDescription) return null
      const lower = extentDescription.toLowerCase()
      for (const entry of REGION_MAP) {
        if (entry.keys.some(k => lower.includes(k))) return entry.img
      }
      return null
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      })
    },
    goBack() {
      this.$bvModal.hide('datasetSelection')
      this.$emit('returnHome')
    },
    getSelsets() {
      this.selsets = []
      let partNums = []
      this.releases.forEach(release => {
        if (release.release_label == this.selectedDataset.releaseLabel) {
          partNums.push(release.part_number)
        }
      })
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/' + this.apiUrl + '/selset', {
            method: 'GET',
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              partNumbers: partNums
            }
          })
          .then(response => {
            const content = response.data
            if (!content.errors) {
              this.selsets = []
              content.rows.forEach(row => {
                if (row.part_number != null) {
                  if (
                    row.part_number.toLowerCase() ==
                    this.selectedDataset.partNumber.toLowerCase()
                  ) {
                    this.selsets.push({selset: row.selset, type: row.type})
                  }
                }
              })
            } else {
              console.log('selsets API Call Error: ' + content.errors)
            }
            this.selsetsLoaded.loaded = this.selsets.length > 0
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token.')
      }
    },
    updateANSIDisplayDataset(release, column_id, extent) {
      this.$emit('updateANSILayersPartNumber', release)
      this.$emit('setSelectedDataset', {
        id: release.id,
        releaseLabel: release.release_label,
        partNumber: release.part_number
      })
      this.selsetsLoaded.id = column_id
      if (extent !== this.currentExtent) {
        this.changeExtent(
          this.extents.filter(e => e.extent_description == extent)[0]
        )
      }
    },
    updateAnsiDisplaySelset(selset) {
      this.$emit('selection')
      this.$emit('updateSelset', selset)
      this.$bvModal.hide('datasetSelection')
      this.ansiLayersButtonCtrl.forEach(layer => {
        this.$emit('unloadLayers', layer.name)
      })
    },
    getExtents() {
      if (sessionStorage.token) {
        axios
          .get(process.env.API_LOCATION + '/api/users/getExtents', {
            headers: {
              'x-auth-token': sessionStorage.token
            },
            params: {
              company_id: this.user.org
            }
          })
          .then(response => {
            const content = response.data
            if (content.errors) {
              console.log('getExtents API Call Error: ' + content.errors)
            } else {
              this.extents = content
            }
          })
          .catch(function (error) {
            console.log(error)
          })
      } else {
        console.log('No User Token')
      }
    },
    changeExtent(selectedExtent) {
      let extentValues = []
      let extentCenter = []
      selectedExtent.extent.split(',').forEach(element => {
        extentValues.push(element)
      })
      selectedExtent.extent_center.split(',').forEach(element => {
        extentCenter.push(element)
      })
      let coord = new transformExtent(
        extentValues,
        new projGet('EPSG:4326'),
        new projGet('EPSG:3857')
      )
      let center = new transform(
        extentCenter,
        new projGet('EPSG:4326'),
        new projGet('EPSG:3857')
      )
      let view = new View({
        extent: coord,
        center: center,
        constrainOnlyCenter: true,
        zoom: 5,
        minZoom: 3,
        multiWorld: true
      })
      this.$emit('extentChanged', {
        selectedExtent: selectedExtent.extent_description,
        view: view
      })
    }
  },
  watch: {
    selectedDataset: function (newValue, oldValue) {
      this.getSelsets()
    }
  },
  mounted() {
    this.getExtents()
  }
}
</script>
